import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * FirstSessionFlow — "What actually happens in the first session"
 *
 * Ultra-minimal: giant serif numeric marker + short title + one calm line
 * of body copy per step. No icons, no boxes, no colored backgrounds. The
 * three steps are laid out as a horizontal timeline on desktop and stack
 * vertically on smaller widths.
 */

interface Step {
  index: string;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    index: "01",
    title: "Reach out",
    body:
      "Send a short note about what's on your mind. No paperwork, no forms — just a quiet first sentence, and we'll answer within a day.",
  },
  {
    index: "02",
    title: "A gentle 20-minute call",
    body:
      "Before booking anything, we speak on the phone. This isn't therapy yet — it's a chance to feel the room, ask questions, and see if we're a fit.",
  },
  {
    index: "03",
    title: "Your first session",
    body:
      "We meet for 50 minutes. You share as little or as much as feels right. Nothing is diagnosed, nothing is fixed — we begin, softly.",
  },
];

export default function FirstSessionFlow() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const subtitleRef = useEnterAnimation<HTMLParagraphElement>({ delay: 300 });
  const step1Ref = useEnterAnimation<HTMLDivElement>({ delay: 480 });
  const step2Ref = useEnterAnimation<HTMLDivElement>({ delay: 620 });
  const step3Ref = useEnterAnimation<HTMLDivElement>({ delay: 760 });
  const stepRefs = [step1Ref, step2Ref, step3Ref];

  return (
    <section
      id="first-session"
      className="relative w-full bg-background-100"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta strip */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Our Services / 04
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            The first session
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto">
          {/* Header */}
          <div className="max-w-[900px] mb-24 md:mb-32">
            <div ref={eyebrowRef} className="mb-8">
              <span className="inline-flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 font-medium">
                <span className="w-8 h-[1px] bg-foreground-950/35"></span>
                What actually happens
              </span>
            </div>

            <h2
              ref={titleRef}
              className="mb-6"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.5rem, 5.2vw, 5rem)",
                fontWeight: 300,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
                color: "oklch(var(--foreground-950))",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              Your first session, without surprises.
            </h2>

            <p
              ref={subtitleRef}
              className="italic max-w-[620px]"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(1.05rem, 1.35vw, 1.35rem)",
                fontWeight: 300,
                lineHeight: 1.6,
                letterSpacing: "0.005em",
                color: "oklch(var(--foreground-950) / 0.72)",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              Three quiet steps &mdash; no clipboards, no waiting rooms, no
              pressure to know what you want.
            </p>
          </div>

          {/* Steps — a soft horizontal timeline */}
          <div className="relative">
            {/* Horizontal guide line (desktop only) */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute left-0 right-0 h-[1px] bg-foreground-950/15"
              style={{ top: "3.5rem" }}
            ></div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-14 lg:gap-12">
              {STEPS.map((step, i) => (
                <div
                  key={step.index}
                  ref={stepRefs[i]}
                  className="relative flex flex-col items-start"
                >
                  {/* Guide dot (desktop) */}
                  <span
                    aria-hidden="true"
                    className="hidden lg:block absolute w-2 h-2 rounded-full bg-primary-500"
                    style={{ top: "3rem", left: 0 }}
                  ></span>

                  <div
                    className="mb-6 lg:pl-8"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(3.75rem, 6vw, 6rem)",
                      fontWeight: 300,
                      letterSpacing: "-0.04em",
                      lineHeight: 1,
                      color: "oklch(var(--foreground-950) / 0.35)",
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {step.index}
                  </div>

                  <h3
                    className="mb-4 lg:pl-8"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1.35rem, 1.75vw, 1.85rem)",
                      fontWeight: 400,
                      letterSpacing: "-0.015em",
                      lineHeight: 1.2,
                      color: "oklch(var(--foreground-950))",
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {step.title}
                  </h3>

                  <p
                    className="lg:pl-8 max-w-[360px]"
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                      lineHeight: 1.7,
                      color: "oklch(var(--foreground-950) / 0.82)",
                    }}
                  >
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}